import React, { useState, useEffect } from 'react';
import { PageContentWrapper } from '../main/LayoutStyles';
import {
    FormWrapper, StepIndicator, FormStep, FormField, FormLabel, OptionsGrid,
    FormNavigation, FormNavButton, FinalWarning, FormTextInput, FormTextarea, Tag,
    TagInputWrapper, StepHeader, RecipientCategory, PersonOption
} from './ReportFormStyles';
import { AlertTriangle, Send, ArrowLeft, ArrowRight, User, Users, School, Globe, Home, Smartphone, Upload, Loader2, Check, ChevronDown, ChevronUp } from 'lucide-react';
import apiClient from '../services/api';
import { AlertBox, StyledButton } from '../main/CommonStyles';
import { useNavigate } from 'react-router-dom';

const steps = [
    { id: 1, label: 'Kogo informujesz?' },
    { id: 2, label: 'Co i gdzie się stało?' },
    { id: 3, label: 'Szczegóły i Dowody' },
    { id: 4, label: 'Potwierdź i wyślij' },
];

const locationOptions = [
    { key: 'szkola', label: 'W szkole', icon: School },
    { key: 'internet', label: 'W internecie', icon: Globe },
    { key: 'dom', label: 'W domu / okolicy', icon: Home },
    { key: 'telefon', label: 'Przez telefon / SMS', icon: Smartphone },
];

const ReportForm = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [staff, setStaff] = useState({ psychologists: [], teachers: [] });
    const [loadingStaff, setLoadingStaff] = useState(true);
    const [openCategory, setOpenCategory] = useState(null);
    const [formData, setFormData] = useState({
        recipientId: null,
        problemType: 'other',
        description: '',
        locations: [],
        witnesses: '',
        isAnonymous: false,
        attachments: [],
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchStaff = async () => {
            setLoadingStaff(true);
            try {
                const response = await apiClient.get('/user/organization-staff');
                const psychologists = response.data.filter(p => p.professionalTitle && p.professionalTitle.toLowerCase().includes('psycholog'));
                const teachers = response.data.filter(p => !p.professionalTitle || !p.professionalTitle.toLowerCase().includes('psycholog'));
                setStaff({ psychologists, teachers });
            } catch (err) {
                setError('Nie udało się pobrać listy adresatów zgłoszenia.');
            } finally {
                setLoadingStaff(false);
            }
        };
        fetchStaff();
    }, []);

    const handleNext = () => {
        if (currentStep === 1 && !formData.recipientId) return setError('Musisz wybrać, kogo chcesz poinformować.');
        if (currentStep === 2 && formData.description.length < 10) return setError('Opis musi mieć co najmniej 10 znaków.');
        setError('');
        setCurrentStep(prev => prev + 1);
    };

    const handleBack = () => setCurrentStep(prev => prev - 1);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSelectRecipient = (id) => {
        setFormData(prev => ({ ...prev, recipientId: id }));
    };

    const handleLocationTagClick = (locationKey) => {
        setFormData(prev => {
            const newLocations = prev.locations.includes(locationKey)
                ? prev.locations.filter(loc => loc !== locationKey)
                : [...prev.locations, locationKey];
            return { ...prev, locations: newLocations };
        });
    };

    const handleFileChange = (e) => {
        if (e.target.files) {
            setFormData(prev => ({ ...prev, attachments: [...prev.attachments, ...Array.from(e.target.files)] }));
        }
    };

    const handleSubmit = async () => {
        if (formData.description.length < 10) {
            setError('Twój opis jest zbyt krótki. Postaraj się opisać sytuację bardziej szczegółowo.');
            setCurrentStep(2);
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');

        const submissionData = {
            ...formData,
            attachmentNames: formData.attachments.map(f => f.name),
            attachments: undefined,
        };

        try {
            await apiClient.post('/cases/submit', submissionData);
            setSuccess('Twoje zgłoszenie zostało wysłane. Dziękujemy Ci za odwagę. Otrzymasz pomoc.');
            setCurrentStep(5);
        } catch(err) {
            setError(err.response?.data?.message || 'Nie udało się wysłać zgłoszenia. Spróbuj ponownie.');
        } finally {
            setLoading(false);
        }
    };

    const toggleCategory = (category) => {
        setOpenCategory(prev => (prev === category ? null : category));
    };

    // === POPRAWIONA FUNKCJA Z UZUPEŁNIONYMI KROKAMI ===
    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <FormStep>
                        <StepHeader>
                            <h3>Wybierz zaufaną osobę</h3>
                            <p>Możesz wybrać ogólną grupę (np. "Dowolny Psycholog") lub konkretną osobę z listy, jeśli chcesz.</p>
                        </StepHeader>
                        {loadingStaff ? <div style={{textAlign: 'center'}}><Loader2 className="icon-spin" /></div> : (
                            <>
                                <RecipientCategory>
                                    <div className="category-header" onClick={() => handleSelectRecipient(-1)}>
                                        <span>Rodzic / Opiekun</span>
                                        {formData.recipientId === -1 && <Check />}
                                    </div>
                                </RecipientCategory>
                                <RecipientCategory>
                                    <div className="category-header" onClick={() => toggleCategory('psychologists')}>
                                        <span>Psycholog / Pedagog</span>
                                        {openCategory === 'psychologists' ? <ChevronUp /> : <ChevronDown />}
                                    </div>
                                    {openCategory === 'psychologists' && (
                                        <div className="person-list">
                                            <PersonOption onClick={() => handleSelectRecipient(-2)} $selected={formData.recipientId === -2}>
                                                <Users />
                                                <div className="info"><div className="name">Dowolny Psycholog / Pedagog</div></div>
                                                {formData.recipientId === -2 && <Check />}
                                            </PersonOption>
                                            {staff.psychologists.map(p => (
                                                <PersonOption key={p.id} onClick={() => handleSelectRecipient(p.id)} $selected={formData.recipientId === p.id}>
                                                    <User />
                                                    <div className="info">
                                                        <div className="name">{`${p.firstName} ${p.lastName}`}</div>
                                                        <div className="title">{p.professionalTitle}</div>
                                                    </div>
                                                    {formData.recipientId === p.id && <Check />}
                                                </PersonOption>
                                            ))}
                                        </div>
                                    )}
                                </RecipientCategory>
                                <RecipientCategory>
                                    <div className="category-header" onClick={() => toggleCategory('teachers')}>
                                        <span>Nauczyciel</span>
                                        {openCategory === 'teachers' ? <ChevronUp /> : <ChevronDown />}
                                    </div>
                                    {openCategory === 'teachers' && (
                                        <div className="person-list">
                                            <PersonOption onClick={() => handleSelectRecipient(-3)} $selected={formData.recipientId === -3}>
                                                <Users />
                                                <div className="info"><div className="name">Dowolny Nauczyciel</div></div>
                                                {formData.recipientId === -3 && <Check />}
                                            </PersonOption>
                                            {staff.teachers.map(p => (
                                                <PersonOption key={p.id} onClick={() => handleSelectRecipient(p.id)} $selected={formData.recipientId === p.id}>
                                                    <User />
                                                    <div className="info">
                                                        <div className="name">{`${p.firstName} ${p.lastName}`}</div>
                                                        <div className="title">{p.professionalTitle}</div>
                                                    </div>
                                                    {formData.recipientId === p.id && <Check />}
                                                </PersonOption>
                                            ))}
                                        </div>
                                    )}
                                </RecipientCategory>
                            </>
                        )}
                    </FormStep>
                );
            case 2:
                return (
                    <FormStep>
                        <StepHeader>
                            <h3>Co i gdzie się stało?</h3>
                            <p>Opisz sytuację jak najdokładniej. Wszystko co napiszesz jest ważne.</p>
                        </StepHeader>
                        <FormField>
                            <FormLabel htmlFor="description">Opis sytuacji*</FormLabel>
                            <FormTextarea id="description" name="description" value={formData.description} onChange={handleChange} rows="6" placeholder="Np. Wczoraj na przerwie Ania i Kasia śmiały się ze mnie i zabrały mi plecak..."></FormTextarea>
                        </FormField>
                        <FormField>
                            <FormLabel>Gdzie to się najczęściej dzieje? (możesz zaznaczyć kilka)</FormLabel>
                            <OptionsGrid>
                                {locationOptions.map(opt => (
                                    <Tag key={opt.key} type="button" $selected={formData.locations.includes(opt.key)} onClick={() => handleLocationTagClick(opt.key)}>
                                        <opt.icon />
                                        <span>{opt.label}</span>
                                    </Tag>
                                ))}
                            </OptionsGrid>
                        </FormField>
                    </FormStep>
                );
            case 3:
                return (
                    <FormStep>
                        <StepHeader>
                            <h3>Dodatkowe informacje</h3>
                            <p>Czy ktoś to widział? Czy masz jakieś dowody, np. screeny rozmów?</p>
                        </StepHeader>
                        <FormField>
                            <FormLabel htmlFor="witnesses">Świadkowie (kto to widział?)</FormLabel>
                            <FormTextInput id="witnesses" type="text" name="witnesses" value={formData.witnesses} onChange={handleChange} placeholder="Np. Tomek z 3B, pani z biblioteki"/>
                        </FormField>
                        <FormField>
                            <FormLabel>Załączniki (zdjęcia, screeny, filmy)</FormLabel>
                            <TagInputWrapper>
                                <input type="file" id="attachments" multiple onChange={handleFileChange} style={{display: 'none'}}/>
                                <StyledButton as="label" htmlFor="attachments" $variant="outline"><Upload size={16}/> Dodaj plik</StyledButton>
                                <div className="file-list">
                                    {formData.attachments.map((file, index) => (<span key={index}>{file.name}</span>))}
                                </div>
                            </TagInputWrapper>
                        </FormField>
                    </FormStep>
                );
            case 4:
                return (
                    <FormStep>
                        <StepHeader>
                            <h3>Ostatni krok</h3>
                            <p>Przejrzyj swoje zgłoszenie. Jesteś już prawie na końcu!</p>
                        </StepHeader>
                        <FormField>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '1.2rem'}}>
                                <input type="checkbox" name="isAnonymous" checked={formData.isAnonymous} onChange={(e) => setFormData(prev => ({...prev, isAnonymous: e.target.checked}))} style={{width: '20px', height: '20px'}}/>
                                Chcę zgłosić anonimowo
                            </label>
                        </FormField>
                        <FinalWarning>
                            <AlertTriangle size={32} />
                            <p><strong>Ważne pouczenie!</strong></p>
                            <p>Pamiętaj, aby pisać prawdę. Zgłaszanie nieprawdziwych informacji lub oskarżanie niewinnych osób jest bardzo krzywdzące i może prowadzić do poważnych konsekwencji.</p>
                        </FinalWarning>
                    </FormStep>
                );
            case 5:
                return (
                    <FormStep style={{textAlign: 'center'}}>
                        <StepHeader>
                            <h3>Dziękujemy! Zgłoszenie wysłane.</h3>
                            <p className="step-description">{success}</p>
                        </StepHeader>
                        <StyledButton onClick={() => { setCurrentStep(1); setFormData({recipientId: null, problemType: 'other', description: '', locations: [], witnesses: '', isAnonymous: false, attachments: []}); setSuccess(''); }}>Zgłoś kolejną sprawę</StyledButton>
                    </FormStep>
                )
            default:
                return null;
        }
    };

    return (
        <PageContentWrapper className="custom-scrollbar">
            <FormWrapper>
                {currentStep <= 4 && (
                    <StepIndicator>
                        {steps.map(step => (
                            <div key={step.id} className={`step ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}>
                                <div className="step-icon">{step.id}</div>
                                <div className="step-label">{step.label}</div>
                            </div>
                        ))}
                    </StepIndicator>
                )}

                {error && <AlertBox type="error" style={{marginBottom: '1rem'}}>{error}</AlertBox>}

                {renderStepContent()}

                {currentStep <= 4 && (
                    <FormNavigation>
                        <FormNavButton $variant="outline" onClick={handleBack} disabled={currentStep === 1 || loading}>
                            <ArrowLeft /> Wróć
                        </FormNavButton>
                        {currentStep < 4 ? (
                            <FormNavButton onClick={handleNext} disabled={loading}>
                                Dalej <ArrowRight />
                            </FormNavButton>
                        ) : (
                            <FormNavButton onClick={handleSubmit} disabled={loading} $variant="secondary">
                                {loading ? <Loader2 className="icon-spin" /> : <Send />}
                                Wyślij Zgłoszenie
                            </FormNavButton>
                        )}
                    </FormNavigation>
                )}
            </FormWrapper>
        </PageContentWrapper>
    );
};

export default ReportForm;